import { useMemo, useState } from 'react'
import { Package, Search } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { rankAssortment } from '../lib/assortment-search'
import type { CountAssortmentItem } from '@/api/endpoints/inventory-count-sessions'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { useProducts } from '@/features/products/hooks/use-products'
import { cn } from '@/lib/utils'
import { formatStockDisplay } from '@/shared/lib/stock-quantity'

interface CountSearchSheetProps {
  open: boolean
  onClose: () => void
  /** The location's assortment, held for the session — this search never leaves the device. */
  items: Array<CountAssortmentItem>
  /** Captured totals by SKU, for the "gezählt n" badge and the outstanding-first ranking. */
  countedQtyBySku: Map<string, number>
  onPick: (item: CountAssortmentItem) => void
}

/**
 * Manual search over the counted location's assortment.
 *
 * Everything is local and synchronous: the list is already on the device, so there is no debounce
 * and no request in the typing path — that round trip is what made the old combobox feel broken in
 * a store dead spot. Only the explicit "search the whole catalogue" action talks to the server,
 * once, on tap.
 *
 * This is a deliberate real text field, unlike the quantity pad: a name has to be typed. The
 * drawer keeps `repositionInputs={false}`, which is what stopped vaul from pushing the sheet
 * off-screen when the iOS keyboard resized the visual viewport (round-4 bug), and the field sits
 * at the top of the sheet so the keyboard covers only the results.
 */
export function CountSearchSheet({
  open,
  onClose,
  items,
  countedQtyBySku,
  onPick,
}: CountSearchSheetProps) {
  const { t } = useTranslation('inventory-count')
  const [query, setQuery] = useState('')
  /** The query the counter asked to run against the whole catalogue; null until they ask. */
  const [catalogQuery, setCatalogQuery] = useState<string | null>(null)

  const countedSkus = useMemo(
    () => new Set(countedQtyBySku.keys()),
    [countedQtyBySku],
  )
  const results = useMemo(
    () => rankAssortment(items, countedSkus, query),
    [items, countedSkus, query],
  )

  const trimmed = query.trim()
  const noAssortmentMatch = trimmed !== '' && results.length === 0

  const catalog = useProducts(
    // No restaurant scope: the whole point of this fallback is to look beyond the location.
    { search: catalogQuery ?? undefined, pageSize: 20 },
    { enabled: catalogQuery != null },
  )
  const assortmentSkus = useMemo(
    () => new Set(items.map((item) => item.sku)),
    [items],
  )

  function close() {
    setQuery('')
    setCatalogQuery(null)
    onClose()
  }

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => !next && close()}
      repositionInputs={false}
    >
      <DrawerContent className="h-[80svh]">
        <DrawerHeader className="pb-2 text-left">
          <DrawerTitle className="sr-only">
            {t('search.sheetTitle')}
          </DrawerTitle>
          <div className="flex items-center gap-2">
            <Search
              className="h-5 w-5 shrink-0 text-muted-foreground"
              aria-hidden
            />
            <Input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                setCatalogQuery(null)
              }}
              placeholder={t('search.placeholder')}
              className="h-12 text-base"
              autoComplete="off"
              aria-label={t('search.placeholder')}
            />
          </div>
          <DrawerDescription className="tabular-nums">
            {trimmed === ''
              ? t('search.hintIdle')
              : t('search.hintResults', {
                  hits: results.length,
                  query: trimmed,
                })}
          </DrawerDescription>
        </DrawerHeader>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {results.map((item) => {
            const counted = countedQtyBySku.get(item.sku)
            return (
              <ResultRow
                key={item.sku}
                name={item.name}
                detail={
                  item.category != null
                    ? `${item.sku} · ${item.category}`
                    : item.sku
                }
                badge={
                  counted != null
                    ? t('search.countedBadge', {
                        qty: formatStockDisplay(counted),
                      })
                    : null
                }
                onClick={() => onPick(item)}
              />
            )
          })}

          {noAssortmentMatch && (
            <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
              <p className="text-sm font-medium">{t('search.emptyTitle')}</p>
              <p className="text-sm text-muted-foreground">
                {t('search.emptyBody', { query: trimmed })}
              </p>
              {catalogQuery == null && (
                <Button
                  variant="outline"
                  className="h-12"
                  onClick={() => setCatalogQuery(trimmed)}
                >
                  {t('search.searchCatalogue')}
                </Button>
              )}
              {catalog.isFetching && <Spinner className="size-5" />}
            </div>
          )}

          {catalogQuery != null && !catalog.isFetching && (
            <div className="border-t border-border">
              <p className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('search.catalogueTitle')}
              </p>
              {(catalog.data?.items ?? []).length === 0 && (
                <p className="px-4 py-3 text-sm text-muted-foreground">
                  {t('search.catalogueEmpty')}
                </p>
              )}
              {(catalog.data?.items ?? []).map((product) => {
                const assorted = assortmentSkus.has(product.sku)
                return (
                  <ResultRow
                    key={product.sku}
                    name={product.name}
                    detail={product.sku}
                    // Not carried at this location: the server refuses a capture for it with 404,
                    // so the row says so instead of failing on submit.
                    badge={assorted ? null : t('search.notAtLocation')}
                    disabled={!assorted}
                    onClick={() => {
                      const item = items.find((i) => i.sku === product.sku)
                      if (item) onPick(item)
                    }}
                  />
                )
              })}
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  )
}

function ResultRow({
  name,
  detail,
  badge,
  disabled = false,
  onClick,
}: {
  name: string
  detail: string
  badge: string | null
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex min-h-14 w-full items-center gap-3 border-b border-border px-4 py-2 text-left last:border-0',
        disabled && 'opacity-60',
      )}
    >
      <span className="flex size-10 shrink-0 items-center justify-center rounded-md border border-border bg-muted">
        <Package className="h-5 w-5 text-muted-foreground" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{name}</span>
        <span className="block truncate text-xs tabular-nums text-muted-foreground">
          {detail}
        </span>
      </span>
      {badge != null && (
        <span className="shrink-0 rounded-md bg-secondary px-2 py-1 text-xs font-semibold tabular-nums text-secondary-foreground">
          {badge}
        </span>
      )}
    </button>
  )
}
